import { Activity } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export function SettingsLogsPage() {
  const logs = trpc.aiUsageLogs.list.useQuery({ limit: 100 });
  const summary = trpc.aiUsageLogs.summary.useQuery();

  if (logs.isLoading || summary.isLoading) {
    return (
      <div className="mx-auto w-full max-w-5xl px-4 py-8 md:px-6">
        <UsageLogsHeader />
        <div className="py-8 text-center text-sm text-muted-foreground">Loading…</div>
      </div>
    );
  }

  if (logs.isError) {
    return (
      <div className="mx-auto w-full max-w-5xl px-4 py-8 md:px-6">
        <UsageLogsHeader />
        <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
          {logs.error.message}
        </div>
      </div>
    );
  }

  const rows = logs.data ?? [];
  const summaryRows = summary.data ?? [];
  const grandCost = summaryRows.reduce((acc, r) => acc + r.totalCostUsd, 0);
  const grandTokens = summaryRows.reduce((acc, r) => acc + r.totalTokens, 0);
  const grandRuns = summaryRows.reduce((acc, r) => acc + r.runs, 0);

  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-8 md:px-6">
      <UsageLogsHeader />
      <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-3">
        <SummaryStat label="Total runs" value={grandRuns.toLocaleString("en-US")} />
        <SummaryStat
          label="Total tokens"
          value={grandTokens.toLocaleString("en-US")}
        />
        <SummaryStat label="Estimated cost" value={`$${grandCost.toFixed(4)}`} />
      </div>

      {summaryRows.length > 0 && (
        <div className="overflow-x-auto rounded-lg border">
          <Table className="min-w-[480px]">
            <TableHeader>
              <TableRow>
                <TableHead>Job</TableHead>
                <TableHead className="text-right">Runs</TableHead>
                <TableHead className="text-right">Tokens</TableHead>
                <TableHead className="text-right">Cost (USD)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {summaryRows.map((r) => (
                <TableRow key={r.jobName}>
                  <TableCell className="font-medium">{r.jobName}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {r.runs.toLocaleString("en-US")}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {r.totalTokens.toLocaleString("en-US")}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    ${r.totalCostUsd.toFixed(4)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {rows.length === 0 ? (
        <div className="rounded-lg border border-dashed py-10 text-center text-sm text-muted-foreground">
          <Activity className="mx-auto mb-2 h-6 w-6 opacity-40" />
          No AI calls logged yet.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <Table className="min-w-[820px]">
            <TableHeader>
              <TableRow>
                <TableHead className="w-44">When</TableHead>
                <TableHead>Job</TableHead>
                <TableHead>Model</TableHead>
                <TableHead className="text-right">In</TableHead>
                <TableHead className="text-right">Out</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="text-right">Cost</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                    {formatDateTime(r.createdAt)}
                  </TableCell>
                  <TableCell className="font-medium">{r.jobName}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {r.model}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {r.inputTokens.toLocaleString("en-US")}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {r.outputTokens.toLocaleString("en-US")}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {r.totalTokens.toLocaleString("en-US")}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {r.costUsd != null ? `$${r.costUsd.toFixed(4)}` : "—"}
                  </TableCell>
                  <TableCell>
                    {r.status === "succeeded" ? (
                      <Badge
                        variant="secondary"
                        className="bg-emerald-100 text-emerald-700"
                      >
                        ok
                      </Badge>
                    ) : (
                      <Badge
                        variant="secondary"
                        className="bg-rose-100 text-rose-700"
                        title={r.error ?? undefined}
                      >
                        failed
                      </Badge>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <p className="text-xs text-muted-foreground">
          Showing latest {rows.length} call{rows.length === 1 ? "" : "s"}.
        </p>
      </div>
    </div>
  );
}

function UsageLogsHeader() {
  return (
    <header className="mb-6">
      <h2 className="text-lg font-semibold">AI usage logs</h2>
      <p className="text-sm text-muted-foreground">
        Every call we make to Anthropic on behalf of a job (client data fetch, brief
        generation, …) lands here. Token counts come straight from the API; cost is
        estimated from the model's public pricing.
      </p>
    </header>
  );
}

function SummaryStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border p-3">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="mt-1 text-lg font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function formatDateTime(value: Date | string): string {
  const d = value instanceof Date ? value : new Date(value);
  return d.toLocaleString("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}
